/**
 * Update Banner (Card 10)
 *
 * Prompts native users when a newer build is published to their release channel. Modeled on the
 * PWA service-worker update flow, but driven by the R2 release manifest rather than a `waiting`
 * worker — a native binary has no service-worker update event.
 *
 * Renders nothing on web, offline, or when the manifest is older than or equal to the running
 * build. A mandatory update (running version below `nativeCompat.minNativeVersion`) is
 * non-dismissible: the CTA is the only exit.
 *
 * Contract: src/docs/MOBILE_VERSION_SCHEME.md §5
 */

import { ArrowDownToLine, X } from 'lucide-react';
import { useUpdateAvailable } from '@/hooks/useUpdateAvailable';

export function UpdateBanner() {
  const { updateAvailable, mandatory, manifest, artifact, recordDownload, dismiss } =
    useUpdateAvailable();

  if (!updateAvailable || !manifest || !artifact) {
    return null;
  }

  const handleDownload = async () => {
    // Record first, then navigate. `keepalive` on the beacon lets it survive the webview handing
    // the URL off to the system browser, but awaiting keeps ordering honest when it doesn't.
    await recordDownload();
    window.open(artifact.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      // `alert` (assertive) for a mandatory update the user must act on; `status` (polite) for an
      // optional one, so it does not interrupt a screen-reader mid-sentence.
      role={mandatory ? 'alert' : 'status'}
      aria-live={mandatory ? 'assertive' : 'polite'}
      className="fixed bottom-4 right-4 z-50 max-w-sm"
    >
      <div className="bg-blue-600/95 backdrop-blur-sm text-white rounded-lg shadow-lg p-4">
        <div className="flex items-start gap-3">
          <ArrowDownToLine className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />

          <div className="flex-1 min-w-0">
            <p className="font-semibold">
              {mandatory ? 'Update required' : 'Update available'}
            </p>
            <p className="text-sm text-blue-100 mt-0.5">
              {mandatory
                ? `This version is no longer supported. Update to ${manifest.version} to continue.`
                : `Trade Loop ${manifest.version} is ready to install.`}
            </p>

            <div className="flex items-center gap-2 mt-3">
              <button
                type="button"
                onClick={handleDownload}
                className="bg-white text-blue-700 font-semibold text-sm px-3 py-1.5 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
              >
                Update now
              </button>
              {!mandatory && (
                <button
                  type="button"
                  onClick={dismiss}
                  className="text-sm text-blue-100 px-2 py-1.5 rounded-md hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                >
                  Later
                </button>
              )}
            </div>
          </div>

          {!mandatory && (
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss update notification"
              className="flex-shrink-0 text-blue-100 hover:text-white focus:outline-none focus:ring-2 focus:ring-white rounded"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default UpdateBanner;
